/**
 * ScarcityBadge - FOMO Component
 *
 * Visual badge showing limited availability.
 * "ONLY 47 SPOTS LEFT" with pulsing glow.
 * Uses HVME mediakit colors - 10X Zama!
 */

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

interface ScarcityBadgeProps {
  /** Number of spots/items remaining */
  count: number;
  /** Custom label (default: "spots left") */
  label?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Show icon */
  showIcon?: boolean;
  /** Animate count changes */
  animated?: boolean;
  /** Custom class name */
  className?: string;
}

function getUrgencyLevel(count: number): "low" | "medium" | "high" | "critical" {
  if (count <= 5) return "critical";
  if (count <= 20) return "high";
  if (count <= 50) return "medium";
  return "low";
}

function getUrgencyStyles(level: "low" | "medium" | "high" | "critical") {
  switch (level) {
    case "critical":
      return {
        bg: "bg-hvme-aurora-lilac/30",
        border: "border-hvme-aurora-lilac",
        text: "text-hvme-aurora-lilac",
        glow: "shadow-[0_0_20px_rgba(230,190,250,0.5)]",
        animate: true,
      };
    case "high":
      return {
        bg: "bg-hvme-soft-plasma/20",
        border: "border-hvme-soft-plasma",
        text: "text-hvme-soft-plasma",
        glow: "shadow-[0_0_15px_rgba(220,160,250,0.4)]",
        animate: true,
      };
    case "medium":
      return {
        bg: "bg-hvme-nebula-lavender/15",
        border: "border-hvme-nebula-lavender/50",
        text: "text-hvme-nebula-lavender",
        glow: "",
        animate: false,
      };
    case "low":
    default:
      return {
        bg: "bg-hvme-frosted-iris/10",
        border: "border-hvme-frosted-iris/30",
        text: "text-hvme-frosted-iris",
        glow: "",
        animate: false,
      };
  }
}

export function ScarcityBadge({
  count,
  label = "spots left",
  size = "md",
  showIcon = true,
  animated = true,
  className = "",
}: ScarcityBadgeProps) {
  const [displayCount, setDisplayCount] = useState(count);
  const urgencyLevel = getUrgencyLevel(count);
  const styles = getUrgencyStyles(urgencyLevel);

  // Animate count changes
  useEffect(() => {
    if (!animated || displayCount === count) {
      setDisplayCount(count);
      return;
    }

    // Animate down to new count
    const step = displayCount > count ? -1 : 1;
    const timer = setInterval(() => {
      setDisplayCount((prev) => {
        if (prev === count) {
          clearInterval(timer);
          return prev;
        }
        return prev + step;
      });
    }, 50);

    return () => clearInterval(timer);
  }, [count, animated, displayCount]);

  const sizeClasses = {
    sm: "px-2 py-0.5 text-xs",
    md: "px-3 py-1 text-sm",
    lg: "px-4 py-1.5 text-base",
  };

  const iconSizes = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold border ${sizeClasses[size]} ${styles.bg} ${styles.border} ${styles.text} ${styles.glow} ${styles.animate ? "animate-pulse" : ""} ${className}`}
    >
      {showIcon && (
        <span className={iconSizes[size]}>
          {urgencyLevel === "critical" ? "🔥" : urgencyLevel === "high" ? "⚡" : "✨"}
        </span>
      )}

      <span className="uppercase tracking-wide">
        Only{" "}
        <AnimatePresence mode="wait">
          <motion.span
            key={displayCount}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="font-bold"
          >
            {displayCount.toLocaleString()}
          </motion.span>
        </AnimatePresence>{" "}
        {label}
      </span>

      {urgencyLevel === "critical" && (
        <motion.span
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          className={iconSizes[size]}
        >
          !
        </motion.span>
      )}
    </motion.div>
  );
}

/**
 * Larger featured scarcity display
 */
export function ScarcityDisplay({
  count,
  total,
  label = "Genesis Node Spots",
  className = "",
}: {
  count: number;
  total: number;
  label?: string;
  className?: string;
}) {
  const percentage = Math.round((count / total) * 100);
  const urgencyLevel = getUrgencyLevel(count);
  const styles = getUrgencyStyles(urgencyLevel);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`text-center p-6 rounded-2xl bg-hvme-deep-ink/60 backdrop-blur-lg border ${styles.border} ${styles.glow} ${className}`}
    >
      {/* Count Display */}
      <div className="mb-2">
        <motion.span
          className={`text-5xl md:text-6xl font-bold ${styles.text}`}
          animate={styles.animate ? { scale: [1, 1.02, 1] } : {}}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {count.toLocaleString()}
        </motion.span>
        <span className="text-2xl text-hvme-text-muted"> / {total.toLocaleString()}</span>
      </div>

      {/* Label */}
      <p className="text-sm text-hvme-text-secondary mb-4">{label} Remaining</p>

      {/* Mini Progress Bar */}
      <div className="w-full h-2 rounded-full bg-hvme-bg-surface overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{
            background: `linear-gradient(90deg, ${styles.text.includes("aurora") ? "#E6BEFA" : styles.text.includes("plasma") ? "#DCA0FA" : "#E6C8FA"}, transparent)`,
          }}
          initial={{ width: 0 }}
          animate={{ width: `${100 - percentage}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
      </div>

      {/* Urgency Message */}
      {urgencyLevel !== "low" && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className={`mt-4 text-sm font-medium ${styles.text}`}
        >
          {urgencyLevel === "critical"
            ? "🔥 Almost sold out! Act now!"
            : urgencyLevel === "high"
            ? "⚡ Filling up fast!"
            : "✨ Limited availability"}
        </motion.p>
      )}
    </motion.div>
  );
}

export default ScarcityBadge;
